import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { compartilharTexto } from "@/lib/whatsapp";
import api, { brl, fmtDate, fmtTime, rotuloParcela } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ContractForm } from "@/pages/Contracts";
import { Button } from "@/components/ui/button";
import { FREQ } from "@/lib/status";
import { freqLabel } from "@/components/DiasSemana";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Pencil, FileDown, PenLine, CheckCircle2, Link2, Send, Trash2, Share2, ChevronDown, ChevronUp, BadgeCheck, Handshake } from "lucide-react";
import { toast } from "sonner";
import { QuitacaoDialog } from "@/components/QuitacaoDialog";
import { GarantiaPanel } from "@/components/GarantiaPanel";
import { AssinaturaPanel } from "@/components/AssinaturaPanel";
import { EdicaoContratoPanel } from "@/components/EdicaoContratoPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ContractLinkDialog } from "@/components/ContractLinkDialog";
import { ContractPreviewDialog } from "@/components/ContractPreviewDialog";
import { BotaoArquivo } from "@/components/BotaoArquivo";
import { abrirBlob } from "@/lib/docs";
import { ComunicarClienteDialog } from "@/components/ComunicarClienteDialog";
import { LiberacaoDialog } from "@/components/LiberacaoDialog";
import { useAuth } from "@/context/AuthContext";
import { useRecurso, MSG_SEM_RECURSO } from "@/lib/recursos";

export function DeleteContractDialog({ contract, onDeleted, trigger }) {
  const [busy, setBusy] = useState(false);
  const del = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    try {
      await api.delete(`/contracts/${contract.id}`);
      toast.success("Contrato excluído");
      onDeleted && onDeleted();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao excluir"); }
    finally { setBusy(false); }
  };
  const ativo = contract.status !== "contrato_pendente";
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir contrato {contract.numero}?</AlertDialogTitle>
          <AlertDialogDescription>
            {ativo
              ? "Este contrato já está assinado. Todas as parcelas, pagamentos e acordos vinculados serão apagados permanentemente. Esta ação não pode ser desfeita."
              : "O contrato pendente será apagado permanentemente."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={del} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="delete-contract-confirm-btn">{busy ? "Excluindo..." : "Excluir"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = ["administrador", "financeiro"].includes(user?.role);
  const temAcordos = useRecurso("acordos"), temQuitacao = useRecurso("quitacao_antecipada");
  const [c, setC] = useState(null);
  const [signing, setSigning] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get(`/contracts/${id}`); setC(data); }, [id]);
  useEffect(() => { load(); }, [load]);

  const [pdfEstado, setPdfEstado] = useState("idle");
  const openPdf = async () => {
    if (pdfEstado !== "idle" && pdfEstado !== "erro") return;
    setPdfEstado("preparando");
    try {
      await abrirBlob(`/contracts/${id}/pdf`, `${c?.numero || "contrato"}.pdf`, () => setPdfEstado("baixando"));
      setPdfEstado("idle");
    } catch (e) { setPdfEstado("erro"); toast.error(e.response?.data?.detail || "Erro ao gerar PDF", { action: { label: "Tentar novamente", onClick: openPdf } }); }
  };

  const sign = async () => {
    setSigning(true);
    try {
      await api.post(`/contracts/${id}/sign`);
      toast.success("Contrato assinado! Parcelas geradas.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSigning(false); }
  };

  const ensureSignLink = async () => {
    const { data } = await api.post(`/contracts/${id}/sign-link`);
    return `${window.location.origin}/assinar/${data.token}`;
  };
  const copySignLink = async () => {
    const url = await ensureSignLink();
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success("Link de assinatura copiado!", { description: url });
  };
  const [share, setShare] = useState(false);
  const [preview, setPreview] = useState(false);
  const [textDlg, setTextDlg] = useState(false);
  const [quitar, setQuitar] = useState(false);
  const [quit, setQuit] = useState(null);
  const [estQuit, setEstQuit] = useState(false);
  const [motivoQuit, setMotivoQuit] = useState("");
  useEffect(() => { api.get(`/contracts/${id}/quitacao`).then((r) => setQuit(r.data)).catch(() => setQuit(null)); }, [id, c?.status]);
  const estornarQuit = async () => {
    try { await api.post(`/quitacoes/${quit.id}/estornar`, { motivo: motivoQuit }); toast.success("Quitação estornada — parcelas e contrato restaurados."); setEstQuit(false); setMotivoQuit(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao estornar"); }
  };
  const abrirReciboQuit = null;
  const closeText = useCallback(() => setTextDlg(false), []);
  const [resending, setResending] = useState(false);
  const [showEnvios, setShowEnvios] = useState(false);
  const compartilharLink = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/contracts/${id}/resend-sign-link`);
      if (data.mensagem?.texto) await compartilharTexto({ texto: data.mensagem.texto, titulo: "Link de assinatura", mensagem_id: data.mensagem.id });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao gerar link"); }
    finally { setResending(false); }
  };
  const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  if (!c) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin gold-text" /></div>;
  const editable = c.status === "contrato_pendente";

  return (
    <div className="space-y-5 fade-up" data-testid="contract-detail-page">
      <button onClick={() => navigate("/contratos")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-black">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="trt-card p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-extrabold tracking-tight mono">{c.numero}</h1>
              <StatusBadge status={c.status} />
            </div>
            <p className="text-slate-500 mt-1 cursor-pointer hover:underline" onClick={() => navigate(`/clientes/${c.cliente_id}`)}>{c.cliente_nome} • {c.cpf}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {editable && <ContractForm initial={c} onSaved={load} trigger={<Button variant="outline" className="gap-2" data-testid="edit-contract-btn"><Pencil className="w-4 h-4" /> Editar</Button>} />}
            {canDelete && <DeleteContractDialog contract={c} onDeleted={() => navigate("/contratos")} trigger={<Button variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-contract-btn"><Trash2 className="w-4 h-4" /> Excluir</Button>} />}
            <Button variant="outline" onClick={() => setPreview(true)} disabled={pdfEstado === "preparando" || pdfEstado === "baixando"} className="gap-2" data-testid="contract-pdf-btn" title="Prévia com dados reais, revisão e geração do PDF">{pdfEstado === "preparando" ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparando documento…</> : pdfEstado === "baixando" ? <><Loader2 className="w-4 h-4 animate-spin" /> Baixando…</> : <><FileDown className="w-4 h-4" /> Gerar contrato</>}</Button>
            <Button variant="outline" onClick={() => setTextDlg(true)} className="gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="contract-text-btn"><Share2 className="w-4 h-4" /> Comunicar cliente</Button>
            {canDelete && c.status === "emprestimo_ativo" && <Button variant="outline" disabled={!temAcordos} title={temAcordos ? "" : MSG_SEM_RECURSO} onClick={() => navigate(`/acordos/novo?contrato_id=${c.id}`)} className="gap-2 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="contract-fazer-acordo-btn"><Handshake className="w-4 h-4" /> Fazer acordo{!temAcordos && <span className="text-[10px] font-normal text-slate-500">— {MSG_SEM_RECURSO}</span>}</Button>}
            {canDelete && c.status === "emprestimo_ativo" && <LiberacaoDialog c={c} onDone={load} />}
            {canDelete && c.status === "emprestimo_ativo" && <Button variant="outline" disabled={!temQuitacao} title={temQuitacao ? "" : MSG_SEM_RECURSO} onClick={() => setQuitar(true)} className="gap-2 border-gold/60 text-onyx hover:bg-amber-50" data-testid="contract-quitar-btn"><BadgeCheck className="w-4 h-4 gold-text" /> Quitar antecipadamente{!temQuitacao && <span className="text-[10px] font-normal text-slate-500">— {MSG_SEM_RECURSO}</span>}</Button>}
            {editable && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black gap-2" data-testid="sign-contract-btn"><PenLine className="w-4 h-4" /> Registrar Assinatura</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmar assinatura do contrato?</AlertDialogTitle>
                    <AlertDialogDescription>Isto marcará o contrato como assinado, ativará o empréstimo e gerará {c.num_parcelas} parcelas automaticamente. Após assinado, o contrato não poderá ser editado.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={sign} disabled={signing} className="bg-onyx" data-testid="sign-confirm-btn">{signing ? "Processando..." : "Confirmar"}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
        {c.status === "contrato_pendente" && c.link_enviado_em && (
          <div className="mt-3 text-xs text-slate-500" data-testid="contract-link-last-sent">
            <button type="button" onClick={() => setShowEnvios((v) => !v)} className="flex items-center gap-1.5 flex-wrap hover:text-slate-800" data-testid="contract-envios-toggle">
              <Share2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> <span className="whitespace-nowrap">Último envio do link: <b className="text-slate-700">{fmtDT(c.link_enviado_em)}</b></span>
              {(c.link_envios || []).length > 0 && <span className="whitespace-nowrap">• {c.link_envios.length} envio(s)</span>}
              {showEnvios ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showEnvios && (
              <ul className="mt-2 rounded-lg border divide-y max-h-48 overflow-y-auto" data-testid="contract-envios-list">
                {[...(c.link_envios || [])].reverse().map((e, i) => (
                  <li key={`${e.data}-${i}`} className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="contract-envio-item">
                    <span className="mono text-slate-700 whitespace-nowrap">{fmtDT(e.data)}</span>
                    <span className="truncate flex-1 min-w-[6rem]">{e.por}</span>
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${e.canal === "api" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{e.canal === "api" ? "API (legado)" : "manual"}</span>
                      {e.tipo === "lembrete" && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">Lembrete auto</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {c.status === "contrato_pendente" && (
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="contract-observacao-assinatura">
            <b>Observação:</b> A assinatura é opcional e somente será exigida do cliente quando a empresa a solicitar pelo painel abaixo. Salvar ou disponibilizar o contrato para consulta não gera pendência de assinatura.
          </div>
        )}
        {c.status !== "contrato_pendente" && (
          <div className="mt-4 flex items-center gap-2 text-sm text-emerald-600" data-testid="contract-assinatura-status">
            <CheckCircle2 className="w-4 h-4" /> {c.assinado_em ? `Assinado em ${fmtDate(c.assinado_em)} ${fmtTime(c.assinado_em)}` : `Assinatura dispensada pela empresa — ativado em ${c.ativado_em ? `${fmtDate(c.ativado_em)} ${fmtTime(c.ativado_em)}` : fmtDate(c.data_contrato)}`}
            {c.assinatura && <span className="text-slate-400">• por {c.assinatura.nome} • IP {c.assinatura.ip}</span>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Box l="Valor Liberado" v={brl(c.valor_liberado)} />
        <Box l="Valor Total" v={brl(c.valor_total)} gold />
        <Box l="Valor da Parcela" v={brl(c.valor_parcela)} />
        <Box l="Total Recebido" v={brl(c.financeiro?.total_recebido)} />
        {(c.financeiro?.total_desconto > 0) && <Box l="Desconto concedido" v={brl(c.financeiro.total_desconto)} />}
        {(c.financeiro?.encargos_recebidos > 0) && <Box l="Encargos recebidos" v={brl(c.financeiro.encargos_recebidos)} />}
        <Box l="Saldo Restante" v={brl(c.financeiro?.saldo_restante)} />
        <Box l="Taxa de Juros" v={`${c.taxa_juros}%`} />
        <Box l="Multa" v={`${c.multa}%`} />
        <Box l="Frequência" v={freqLabel(c, FREQ)} />
        {c.editado_em && <Box l="Alterado" v={`v${c.versao} • ${fmtDate(c.editado_em)} • ${c.editado_por}`} />}
        <Box l="Parcelas" v={`${c.num_parcelas}x`} />
        <Box l="1º Vencimento" v={fmtDate(c.primeiro_vencimento)} />
        <Box l="Último Vencimento" v={fmtDate(c.ultimo_vencimento)} />
        <Box l="Data do Contrato" v={fmtDate(c.data_contrato)} />
      </div>

      {quit && c.status === "finalizado" && (
        <div className="trt-card p-4 text-sm flex items-center justify-between gap-3 flex-wrap border-emerald-200" data-testid="contract-quitacao-box">
          <div><b className="flex items-center gap-1"><BadgeCheck className="w-4 h-4 text-emerald-600" /> Quitado antecipadamente em {fmtDate(quit.data)}</b>
            <div className="text-xs text-slate-500 mt-0.5">Recebido <b className="mono">{brl(quit.total_recebido)}</b> • desconto de juros futuros <b className="mono">{brl(quit.desconto)}</b> • {quit.parcelas?.length} parcela(s) • {quit.forma_pagamento}</div></div>
          <div className="flex gap-2"><BotaoArquivo size="sm" variant="outline" acao={(ab) => abrirBlob(`/quitacoes/${quit.id}/recibo`, `recibo-${c.numero}.pdf`, ab)} preparando="Preparando recibo…" testid="contract-quitacao-recibo">Recibo único</BotaoArquivo>
            {canDelete && <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => setEstQuit(true)} data-testid="contract-quitacao-estornar">Estornar quitação</Button>}</div>
        </div>
      )}
      <AssinaturaPanel tipo="contrato" id={c.id} onChange={load} />
      {c.status === "emprestimo_ativo" && <EdicaoContratoPanel c={c} onChange={load} />}
      {c.garantia_opcao === "sim" && <GarantiaPanel contrato={c} onChange={load} />}
      {c.observacoes && <div className="trt-card p-4 text-sm"><span className="text-slate-500">Observações: </span>{c.observacoes}</div>}
      {c.garantias && <div className="trt-card p-4 text-sm"><span className="text-slate-500">Garantias: </span>{c.garantias}</div>}

      {c.parcelas?.length > 0 && (
        <div className="trt-card overflow-hidden">
          <div className="px-4 py-3 font-bold border-b">Cronograma de Parcelas</div>
          <div className="md:hidden divide-y" data-testid="cronograma-mobile">
            {c.parcelas.map((p) => (
              <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{rotuloParcela(p).replace("Parcela #", "#")} <span className="font-normal text-slate-500">• {fmtDate(p.vencimento)}</span></div>
                  <div className="text-xs text-slate-500">{p.horario_vencimento || c.horario_vencimento || "18:00"}{p.data_pagamento ? ` • pago em ${fmtDate(p.data_pagamento)}` : ""}</div>
                </div>
                <div className="text-right shrink-0"><div className="font-bold mono text-sm">{brl(p.valor)}</div><StatusBadge type="inst" status={p.status} vencimento={p.vencimento} /></div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-3 py-2">Parc.</th><th className="text-left px-3 py-2">Vencimento</th>
                <th className="text-right px-3 py-2">Valor</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2 hidden md:table-cell">Pago em</th></tr>
            </thead>
            <tbody>
              {c.parcelas.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="px-3 py-2 whitespace-nowrap">{rotuloParcela(p).replace("Parcela #", "#")}</td>
                  <td className="px-3 py-2"><div className="whitespace-nowrap">{fmtDate(p.vencimento)}</div><div className="text-xs text-slate-500 whitespace-nowrap">{p.horario_vencimento || c.horario_vencimento || "18:00"}</div></td>
                  <td className="px-3 py-2 font-medium text-right whitespace-nowrap mono">{brl(p.valor)}</td>
                  <td className="px-3 py-2"><StatusBadge type="inst" status={p.status} vencimento={p.vencimento} /></td>
                  <td className="px-3 py-2 hidden md:table-cell text-slate-500 whitespace-nowrap">{p.data_pagamento ? fmtDate(p.data_pagamento) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
      {preview && <ContractPreviewDialog contract={c} onClose={() => setPreview(false)} onPdf={() => { openPdf(); setPreview(false); }} />}
      {textDlg && <ComunicarClienteDialog contexto="contrato" contratoId={c.id} onClose={closeText} />}
      {quitar && <QuitacaoDialog contrato={c} onClose={() => setQuitar(false)} onDone={load} />}
      <Dialog open={estQuit} onOpenChange={setEstQuit}>
        <DialogContent data-testid="estorno-quitacao-modal">
          <DialogHeader><DialogTitle>Estornar quitação antecipada?</DialogTitle><DialogDescription>Todos os pagamentos desta quitação serão estornados, as parcelas voltam a ficar em aberto e o contrato volta a ativo.</DialogDescription></DialogHeader>
          <Textarea rows={3} value={motivoQuit} onChange={(e) => setMotivoQuit(e.target.value)} placeholder="Motivo do estorno *" data-testid="estorno-quitacao-motivo" />
          <DialogFooter><Button variant="outline" onClick={() => setEstQuit(false)}>Cancelar</Button><Button onClick={estornarQuit} disabled={motivoQuit.trim().length < 3} className="bg-red-600 hover:bg-red-700" data-testid="estorno-quitacao-confirmar">Estornar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const Box = ({ l, v, gold }) => (
  <div className={`trt-card p-3 md:p-4 min-w-0 ${gold ? "gold-border" : ""}`}><div className="text-xs text-slate-500">{l}</div><div className={`font-extrabold mt-0.5 text-sm md:text-base box-mono whitespace-nowrap ${gold ? "gold-text" : ""}`}>{v}</div></div>
);
